import { useState } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useCutlery, type Difficulty, type CutleryMode } from './useCutlery'
import { useTableSetting } from './useTableSetting'
import { useCountCutlery } from './useCountCutlery'
import { CutleryDefs } from './CutlerySVGs'
import GameSetupScreen from '../GameSetupScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import { ss } from '../gameSetupStyles'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import { s } from './cutleryStyles'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import { DrawerPlayScreen } from './DrawerPlayScreen'
import { TablePlayScreen } from './TablePlayScreen'
import { CountPlayScreen } from './CountPlayScreen'

interface CutleryConfig { mode: CutleryMode; difficulty: Difficulty }

interface CutleryGameProps {
  onBack: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']
const MODES: CutleryMode[]       = ['drawer', 'table', 'count']

/* ── Root ─────────────────────────────────────────────────────── */
export default function CutleryGame({ onBack }: CutleryGameProps) {
  const { t }       = useTranslation()
  const sessionMode = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<CutleryConfig>('cutlery', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [mode,       setMode]       = useState<CutleryMode>((sessionMode?.config.mode as CutleryMode) ?? restored?.mode ?? 'drawer')
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')

  const drawerHook = useCutlery(difficulty)
  const tableHook  = useTableSetting(difficulty)
  const countHook  = useCountCutlery(difficulty)

  const { sessionId } = useGameSession({
    activitySlug: 'cutlery',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  /* ── Per-mode completion ── */
  const { gameComplete: drawerComplete, moves: drawerMoves, startTime: drawerStart, endTime: drawerEnd } = drawerHook.state
  const { saveError: drawerError, clearSaveError: clearDrawerError } = useGameCompletion({
    gameComplete:  mode === 'drawer' && drawerComplete,
    activitySlug:  'cutlery',
    difficulty,
    moves:         drawerMoves,
    startTime:     drawerStart,
    endTime:       drawerEnd,
    pairsTotal:    drawerHook.state.total,
    pairsMatched:  drawerHook.state.total,
    mode,
    sessionId,
    onCompleted:   () => { clear(); setScreen('complete') },
  })

  const { gameComplete: tableComplete, moves: tableMoves, startTime: tableStart, endTime: tableEnd, covers } = tableHook.state
  const tableTotal = covers.reduce((n, c) => n + c.order.required.length, 0)
  const { saveError: tableError, clearSaveError: clearTableError } = useGameCompletion({
    gameComplete:  mode === 'table' && tableComplete,
    activitySlug:  'cutlery',
    difficulty,
    moves:         tableMoves,
    startTime:     tableStart,
    endTime:       tableEnd,
    pairsTotal:    tableTotal,
    pairsMatched:  tableTotal,
    mode,
    sessionId,
    onCompleted:   () => { clear(); setScreen('complete') },
  })

  const { gameComplete: countComplete, moves: countMoves, startTime: countStart, endTime: countEnd } = countHook.state
  const { saveError: countError, clearSaveError: clearCountError } = useGameCompletion({
    gameComplete:  mode === 'count' && countComplete,
    activitySlug:  'cutlery',
    difficulty,
    moves:         countMoves,
    startTime:     countStart,
    endTime:       countEnd,
    pairsTotal:    1,
    pairsMatched:  1,
    mode,
    sessionId,
    onCompleted:   () => { clear(); setScreen('complete') },
  })

  const saveError = drawerError || tableError || countError
  const clearSaveError = () => { clearDrawerError(); clearTableError(); clearCountError() }

  const handleStart = () => {
    if (mode === 'drawer') drawerHook.resetGame()
    else if (mode === 'table') tableHook.resetGame()
    else countHook.resetGame()
    saveConfig({ mode, difficulty })
    setScreen('playing')
  }

  const handlePlayAgain = () => {
    if (mode === 'drawer') drawerHook.resetGame()
    else if (mode === 'table') tableHook.resetGame()
    else countHook.resetGame()
    saveConfig({ mode, difficulty })
    setScreen('playing')
  }

  const activeHook = { drawer: drawerHook, table: tableHook, count: countHook }[mode]
  const endTime = activeHook.state.endTime
  const moves   = activeHook.state.moves
  const start   = activeHook.state.startTime

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}
      <CutleryDefs />

      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          onModeChange={setMode}
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}

      {screen === 'playing' && mode === 'drawer' && (
        <DrawerPlayScreen
          state={drawerHook.state}
          onSelectItem={drawerHook.selectItem}
          onPlaceInSlot={drawerHook.placeInSlot}
          onPlaceItemById={drawerHook.placeItemById}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}

      {screen === 'playing' && mode === 'table' && (
        <TablePlayScreen
          state={tableHook.state}
          onSelectItem={tableHook.selectItem}
          onPlaceInCover={tableHook.placeInCover}
          onPlaceItemById={tableHook.placeItemById}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}

      {screen === 'playing' && mode === 'count' && (
        <CountPlayScreen
          state={countHook.state}
          onAddPiece={countHook.addPiece}
          onRemovePiece={countHook.removePiece}
          onCheckAnswer={countHook.checkAnswer}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}

      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🍴"
          title={t.cutlery.complete.title}
          subtitle={mode === 'table' ? t.cutlery.complete.subtitleTable : mode === 'count' ? t.cutlery.complete.subtitleCount : t.cutlery.complete.subtitle}
          stats={[
            { value: String(moves), label: t.cutlery.complete.movesLabel },
            { value: formatGameTime(start, endTime), label: t.cutlery.complete.timeLabel },
            { value: t.cutlery.difficulty[difficulty].label, label: t.cutlery.complete.levelLabel },
          ]}
          playAgainLabel={t.cutlery.complete.playAgain}
          changeLevelLabel={t.cutlery.complete.changeLevel}
          backLabel={t.cutlery.complete.backToDashboard}
          onPlayAgain={handlePlayAgain}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ─────────────────────────────────────────────── */
function SetupScreen({ mode, onModeChange, difficulty, onDifficultyChange, onStart, onBack }: {
  mode:               CutleryMode
  onModeChange:       (m: CutleryMode) => void
  difficulty:         Difficulty
  onDifficultyChange: (d: Difficulty) => void
  onStart:            () => void
  onBack:             () => void
}) {
  const { t } = useTranslation()
  const c = t.cutlery

  return (
    <GameSetupScreen
      title={c.title}
      subtitle={c.subtitle}
      backLabel={c.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...c.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={c.chooseDifficultyTitle}
      startLabel={c.startBtn}
      onStart={onStart}
    >
      {/* Mode selector */}
      <section>
        <h2 style={ss.optionLabel}>{c.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map((m) => (
            <button
              key={m}
              onClick={() => onModeChange(m)}
              style={{ ...ss.modeCard, ...(mode === m ? ss.modeCardActive : {}) }}
              aria-pressed={mode === m}
            >
              <span style={s.modeEmoji}>{m === 'drawer' ? '🗂️' : m === 'table' ? '🍽️' : '🔢'}</span>
              <span style={s.modeLabel}>{c.modes[m].label}</span>
              <span style={s.modeDesc}>{c.modes[m].description}</span>
            </button>
          ))}
        </div>
      </section>
    </GameSetupScreen>
  )
}

